import { graphqlRequest } from '~/adapters/graphqlRequest';
import { FetchAttributeResponsesDocument, FetchAttributeResponsesQueryResult } from '~/gql/graphql';

export interface AttributeFilter {
  id: number;
  values: string[];
}

interface ResolvePeopleLeaderFilterParams {
  companyId: number;
  languageId: number;
  surveyIds: number[];
  /** Null for companies without a people leader attribute; resolution is then a no-op. */
  peopleLeaderAttributeId: number | null;
  attributeFilters?: AttributeFilter[];
  isHierarchyReport?: boolean;
}

export interface ResolvedPeopleLeaderFilter {
  /** companyEmployeeIds the backend filters on, empty when no people leader was requested. */
  managerReportIds: string[];
  /**
   * For the GraphQL reporting endpoints, which take the people leader separately as
   * `managerReportIds` and derive their reports from it. Leaving the filter in place here would
   * narrow a hierarchy report back down to direct reports.
   */
  attributeFilters: AttributeFilter[];
  /**
   * For the `base-report` REST endpoint, which has no `managerReportIds` parameter and instead
   * splits the people leader out of `selectedAttributes` itself, in
   * `reporting-service.separatePeopleLeadersFromAttributes`. It needs the filter kept in place, with
   * names replaced by companyEmployeeIds.
   */
  attributeFiltersWithLeaderIds: AttributeFilter[];
  /**
   * Set when a requested name could not be matched to a leader. Callers must return this to the
   * model instead of querying, because an unresolved name silently produces either zero rows or
   * whole-survey data rather than an error.
   */
  error?: string;
}

/**
 * Translates people leader names into the companyEmployeeIds the reporting backend filters on.
 *
 * The model supplies a person's name because that is what the user typed and what the reporting UI
 * displays, but every reporting endpoint filters the people leader attribute by companyEmployeeId.
 */
export async function resolvePeopleLeaderFilter({
  companyId,
  languageId,
  surveyIds,
  peopleLeaderAttributeId,
  attributeFilters,
  isHierarchyReport,
}: ResolvePeopleLeaderFilterParams): Promise<ResolvedPeopleLeaderFilter> {
  const filters = attributeFilters ?? [];

  const requestedNames = filters
    .filter((filter) => isPeopleLeaderFilter(filter, peopleLeaderAttributeId))
    .flatMap((filter) => filter.values);

  if (requestedNames.length === 0) {
    return { managerReportIds: [], attributeFilters: filters, attributeFiltersWithLeaderIds: filters };
  }

  const labelToIds = await fetchPeopleLeaderLabelToIdMap({
    companyId,
    languageId,
    surveyIds,
    peopleLeaderAttributeId,
    isHierarchyReport,
  });

  const knownIds = new Set<string>();
  labelToIds.forEach((ids) => ids.forEach((id) => knownIds.add(id)));

  const managerReportIds: string[] = [];
  const unresolvedNames: string[] = [];
  const ambiguousNames: { name: string; matchCount: number }[] = [];

  requestedNames.forEach((name) => {
    const ids = labelToIds.get(name);

    if (ids && ids.length === 1) {
      managerReportIds.push(ids[0]);
      return;
    }

    // Two employees can share a name. Picking one silently would attribute a team's results to the
    // wrong person, which is worse than refusing, so the caller is told to disambiguate.
    if (ids && ids.length > 1) {
      ambiguousNames.push({ name, matchCount: ids.length });
      return;
    }

    // The value is meant to be a person's name, but accept a companyEmployeeId that matches a known
    // leader so a caller supplying an id still gets the right answer instead of an error. This is
    // also the escape hatch for an ambiguous name.
    if (knownIds.has(name)) {
      managerReportIds.push(name);
      return;
    }

    unresolvedNames.push(name);
  });

  const otherFilters = filters.filter((filter) => !isPeopleLeaderFilter(filter, peopleLeaderAttributeId));

  if (ambiguousNames.length > 0) {
    return {
      managerReportIds: [],
      attributeFilters: otherFilters,
      attributeFiltersWithLeaderIds: otherFilters,
      error: buildAmbiguousNameError(ambiguousNames),
    };
  }

  if (unresolvedNames.length > 0) {
    return {
      managerReportIds: [],
      attributeFilters: otherFilters,
      attributeFiltersWithLeaderIds: otherFilters,
      error: buildUnresolvedNameError(unresolvedNames, Array.from(labelToIds.keys())),
    };
  }

  const attributeFiltersWithLeaderIds = filters.map((filter) =>
    isPeopleLeaderFilter(filter, peopleLeaderAttributeId) ? { ...filter, values: managerReportIds } : filter,
  );

  return { managerReportIds, attributeFilters: otherFilters, attributeFiltersWithLeaderIds };
}

function isPeopleLeaderFilter(filter: AttributeFilter, peopleLeaderAttributeId: number | null): boolean {
  return peopleLeaderAttributeId !== null && Number(filter.id) === Number(peopleLeaderAttributeId);
}

/**
 * `requestManagerHierarchyData` is what adds leaders who only have indirect reports to the returned
 * value list. Without it the list holds only leaders that some participant reports to directly, so a
 * senior leader whose own reports are all managers is absent and their name cannot be resolved.
 */
async function fetchPeopleLeaderLabelToIdMap({
  companyId,
  languageId,
  surveyIds,
  peopleLeaderAttributeId,
  isHierarchyReport,
}: Omit<ResolvePeopleLeaderFilterParams, 'attributeFilters'>): Promise<Map<string, string[]>> {
  const response = await graphqlRequest<FetchAttributeResponsesQueryResult>(FetchAttributeResponsesDocument, {
    companyIds: [companyId],
    languageId,
    surveyIds,
    isHierarchyReport: Boolean(isHierarchyReport),
    requestManagerHierarchyData: true,
    includeResponseRates: false,
  });

  // A label maps to a list because two employees can share a name; collapsing to one would make the
  // collision invisible and silently attribute one leader's team to the other.
  const labelToIds = new Map<string, string[]>();

  response?.data?.attributesRatingResponses?.attributes?.forEach((attribute) => {
    if (Number(attribute?.id) !== Number(peopleLeaderAttributeId)) return;

    attribute?.values?.forEach((value) => {
      if (!value?.label || !value?.id) return;
      const ids = labelToIds.get(value.label) ?? [];
      if (!ids.includes(value.id)) ids.push(value.id);
      labelToIds.set(value.label, ids);
    });
  });

  return labelToIds;
}

function buildAmbiguousNameError(ambiguousNames: { name: string; matchCount: number }[]): string {
  // The count travels with the name rather than being looked up again, so there is no absent-key case
  // to defend against here.
  const details = ambiguousNames
    .map(({ name, matchCount }) => `"${name}" matches ${matchCount} different people`)
    .join('; ');

  return `Error: ${details}. This is NOT an anonymity threshold issue. Results cannot be attributed to the right person from the name alone. Tell the user that more than one person on this survey has that name, and ask them to narrow it down with another filter such as department or location, or to use the report filters directly.`;
}

function buildUnresolvedNameError(unresolvedNames: string[], knownLabels: string[]): string {
  const names = unresolvedNames.map((name) => `"${name}"`).join(', ');
  const known = knownLabels.length > 0 ? knownLabels.join(', ') : 'none';

  return `Error: Could not find a people leader named ${names} in the selected survey(s). This is NOT an anonymity threshold issue. Filter the people leader attribute by the person's full name (for example "Ben Scharfe"), never by an employee id, an email address, or their own manager's name. The people leaders available for this survey are: ${known}. Tell the user the name was not found and ask them to pick one of the available people leaders.`;
}
